// Shared design tokens for animation
export const easeOutQuart = [0.25, 1, 0.5, 1];

// Reduced-motion-aware motion initial state
export const motionInitial = (prefersReducedMotion, props = { y: 20 }) =>
  prefersReducedMotion ? { opacity: 0 } : { opacity: 0, ...props };

// Reduced-motion-aware transition
export const motionTransition = (prefersReducedMotion, { duration = 0.5, delay = 0 } = {}) => ({
  duration: prefersReducedMotion ? 0.01 : duration,
  delay: prefersReducedMotion ? 0 : delay,
  ease: easeOutQuart,
});

// Smooth scroll to a CSS selector
export const scrollToSection = (selector) => {
  const element = document.querySelector(selector);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

// Lightweight animation utilities to replace heavy Framer Motion usage
// Only use Framer Motion for complex animations

export const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.6 }
};

export const slideInLeft = {
  initial: { opacity: 0, x: -30 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.6 }
};

export const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.6 }
};

// CSS-based animations for better performance
export const cssAnimations = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  /* Utility animation classes */
  .animate-fade-in-up {
    animation: fadeInUp 0.6s ease-out forwards;
  }

  .animate-fade-in {
    animation: fadeIn 0.6s ease-out forwards;
  }

  .animate-slide-in-left {
    animation: slideInLeft 0.6s ease-out forwards;
  }

  .animate-scale-in {
    animation: scaleIn 0.6s ease-out forwards;
  }

  .animate-pulse-subtle {
    animation: pulse 2s ease-in-out infinite;
  }

  /* Performance optimized transforms */
  .will-change-transform {
    will-change: transform;
  }

  .will-change-opacity {
    will-change: opacity;
  }

  /* Intersection observer triggered animations */
  .animate-on-scroll {
    opacity: 0;
    transform: translateY(30px);
    transition: opacity 0.6s ease-out, transform 0.6s ease-out;
  }

  .animate-on-scroll.is-visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Lightweight intersection observer for scroll animations
export const createScrollAnimationObserver = () => {
  if (typeof window === 'undefined') return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target); // Stop observing once animated
        }
      });
    },
    {
      rootMargin: '50px 0px -100px 0px',
      threshold: 0.1
    }
  );

  // Observe all elements with animate-on-scroll class
  document.querySelectorAll('.animate-on-scroll').forEach(el => {
    observer.observe(el);
  });

  return observer;
};

// Initialize scroll animations
export const initScrollAnimations = () => {
  if (typeof window === 'undefined') return;

  // CSS animations are now in index.css — no runtime injection needed

  // Initialize scroll observer
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', createScrollAnimationObserver);
  } else {
    createScrollAnimationObserver();
  }
};

const animationsUtils = {
  fadeInUp,
  fadeIn,
  slideInLeft,
  scaleIn,
  initScrollAnimations,
  createScrollAnimationObserver
};

export default animationsUtils;